import type { AccountEventRequest, WsEvent } from "@storyboard/web-client";
import { createLogger } from "@/logger";

const log = createLogger("query");

import { store } from "@/store/store";
import type { AccountEvent } from "@/store/types";

/**
 * The composite key is the identity (see `accountEventKey`), so a re-received event keeps the
 * existing row's id and takes the incoming timestamp/availability/volume/trigger — the old ON CONFLICT SET.
 */
function mergeAccountEvent(existing: AccountEvent, incoming: AccountEvent): AccountEvent {
  return {
    ...existing,
    createdAt: incoming.createdAt,
    memberAvailability: incoming.memberAvailability,
    deviceVolume: incoming.deviceVolume,
    triggerType: incoming.triggerType,
  };
}

export async function upsertAccountEvent(e: AccountEventRequest): Promise<void> {
  if (!e) {
    log.debug("upsertAccountEvent: no event");
    return;
  }

  const ae: AccountEvent = {
    id: e.id,
    accountId: e.accountId,
    feedId: e.feedId ?? null,
    createdAt: e.timestamp,
    contentId: e.contentId ?? null,
    itemId: e.feedItemId ?? null,
    name: e.name,
    memberAvailability: "",
    // The request carries no volume reading, and a zero is read as a muted device. Null until the real event merges in.
    deviceVolume: null,
    triggerType: e.triggerType ?? null,
  };

  store.upsert("accountEvent", [ae], mergeAccountEvent);
}

export async function bulkUpsertAccountEvent(records: WsEvent[]) {
  if (!records || records.length === 0) {
    return;
  }
  const data = records
    .map(
      (r): AccountEvent => ({
        id: r.id,
        accountId: r.accountId,
        contentId: r.contentId ?? null,
        createdAt: r.createdAt,
        deviceVolume: r.deviceVolume ?? null,
        feedId: r.feedId ?? null,
        itemId: r.itemId ?? null,
        memberAvailability: r.memberAvailability ?? null,
        name: r.name,
        triggerType: r.triggerType ?? null,
      }),
    )
    // Oldest first so the newest duplicate of a composite key wins the upsert, as before.
    .sort((a, b) => (a.createdAt ?? "").localeCompare(b.createdAt ?? ""));
  store.upsert("accountEvent", data, mergeAccountEvent);
}
